import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/alert';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/form';
import { Input } from '@art-of-hospitality-solutions-llc/ui-kit/components/input';
import { DialogDescription } from '@radix-ui/react-dialog';
import { ArchiveIcon } from 'lucide-react';
import { useUpdateTicketedEvent } from '@Hooks/useUpdateTicketedEvent';
import { toastSuccess } from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';

interface ArchiveEventDialogProps {
  event: { id: string; name: string };
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  /** Optional userId to record who archived the event */
  userId?: string;
}

const archiveEventFormSchema = z.object({
  confirmationText: z.string().min(1, 'Please enter confirmation text'),
});

type ArchiveEventFormValues = z.infer<typeof archiveEventFormSchema>;

export default function ArchiveEventDialog({
  event,
  isOpen,
  setIsOpen,
  userId,
}: ArchiveEventDialogProps) {
  const [isSaving, setIsSaving] = useState(false);
  const updateMutation = useUpdateTicketedEvent({
    onSuccess: () => {
      toastSuccess('Event archived successfully.');
      setIsOpen(false);
    },
    onError: (error) => {
      console.error('Error archiving event:', error);
      archiveEventForm.setError('root', {
        type: 'server',
        message: 'We were unable to archive this event. Please try again.',
      });
    },
  });

  const archiveEventForm = useForm<ArchiveEventFormValues>({
    resolver: zodResolver(archiveEventFormSchema),
    defaultValues: {
      confirmationText: '',
    },
  });

  const onSubmit = async (values: ArchiveEventFormValues) => {
    if (values.confirmationText.toLowerCase() !== 'archive') {
      archiveEventForm.setError('confirmationText', {
        type: 'manual',
        message: 'You must type "ARCHIVE" to confirm.',
      });
      return;
    }

    archiveEventForm.clearErrors('root');

    try {
      setIsSaving(true);
      await updateMutation.mutateAsync({
        eventId: event.id,
        payload: {
          event: {
            archivedAt: new Date().toISOString(),
            archivedBy: userId || null,
          },
        },
      });
      setIsOpen(false);
    } catch (error: any) {
      console.error('Error archiving event:', error);
      archiveEventForm.setError('root', {
        type: 'server',
        message: 'We were unable to archive this event. Please try again.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const confirmationText = archiveEventForm.watch('confirmationText');
  const archiveable = confirmationText.toLowerCase() === 'archive';

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>
            Are you sure you want to archive {event.name}?
          </DialogTitle>
          <DialogDescription className="text-sm text-zinc-500">
            Archiving this event will hide it from all users.
          </DialogDescription>
        </DialogHeader>

        <Form {...archiveEventForm}>
          <form
            onSubmit={archiveEventForm.handleSubmit(onSubmit)}
            className="space-y-6"
          >
            {archiveEventForm.formState.errors.root?.message && (
              <div className="mb-4">
                <Alert variant="destructive">
                  <AlertTitle>Something went wrong</AlertTitle>
                  <AlertDescription>
                    {archiveEventForm.formState.errors.root?.message}
                  </AlertDescription>
                </Alert>
              </div>
            )}

            <div>
              <FormField
                control={archiveEventForm.control}
                name="confirmationText"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Confirm archiving</FormLabel>
                    <FormControl>
                      <Input
                        placeholder='type "ARCHIVE" to confirm'
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="link" disabled={isSaving}>
                  Cancel
                </Button>
              </DialogClose>

              <Button
                type="submit"
                variant="destructive"
                loading={isSaving}
                disabled={isSaving || !archiveable}
                icon={{ src: <ArchiveIcon className="h-4 w-4" /> }}
              >
                Archive
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
